import PropTypes from 'prop-types'
import { cn } from '@/lib/utils'
import React from 'react'
import { HelpCircle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip'

const InputFieldObfuscated = React.forwardRef(
  (
    {
      icon,
      label,
      tooltipHead,
      tooltipBody,
      className,
      helpText,
      onBlur,
      touched,
      type,
      error,
      dataTestId,
      value,
      onChange,
      formatBeforeMaskFN = value => value, // function to format the value before masking
      ...props
    },
    ref
  ) => {
    const [maskedValue, setMaskedValue] = React.useState('')
    const timeoutRef = React.useRef(null)

    const handleChange = e => {
      const inputValue = e.target.value
      const lastChar = inputValue.slice(-1)
      let newValue

      if (inputValue.length < value.length) {
        // Remove last character
        newValue = value.slice(0, -1)
      } else {
        // Append new character
        newValue = value + lastChar
      }

      // update the masked value state with the modified value to show only the last digit
      setMaskedValue(maskValue(formatBeforeMaskFN(newValue)))

      // Mask again after showing the last digit for a short period
      if (inputValue.length > 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          setMaskedValue(previousMaskedValue => (previousMaskedValue || '').replace(/\d/g, '•'))
        }, 2000)
      }
      onChange({ ...e, target: { ...e.target, value: newValue } }) // Updating Formik value
    }

    const handleBlur = e => {
      console.log('Handle blurred triggered')
      setMaskedValue(previousMaskedValue => (previousMaskedValue || '').replace(/\d/g, '•'))
      onBlur && onBlur(e) // Formik's onBlur trigger
    }

    /* 
    Takes in a string mask everyting expect the last char 
    hello -> •••• + o -> ••••o 
    */
    const maskValue = val => {
      if (!val) return ''
      return val.slice(0, -1).replace(/\d/g, '•') + val[val.length - 1]
    }

    return (
      <div className="flex flex-col items-start self-stretch gap-1.5" data-testid="input-field-obfuscated">
        <p className="text-sm text-[--base-black] font-semibold" style={{ color: '#232429' }}>{label}</p>
        <div
          className={cn(
            'flex min-h-10 items-center rounded-md border border-input bg-white pl-3 text-base w-full',
            error && touched ? 'border-[--error-500]' : '',
            className
          )}
          onBlur={handleBlur} // Handle blur event
        >
          {icon}
          <input
            onChange={e => handleChange(e)}
            value={maskedValue}
            ref={ref}
            // onBlur={handleBlur}
            type={type}
            className={`w-full placeholder:text-[--gray-500] placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${icon ? 'p-2' : ''
              }`}
            {...props}
            data-testid={dataTestId}
          />
          <div className="px-2">
            {tooltipHead || tooltipBody ? (
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="cursor-pointer">
                      <HelpCircle
                        className="w-4 h-4"
                        color="#98A2B3"
                        name="help-icon"
                        data-testid="help-icon"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent 
                    className="mb-1 flex flex-col font-sans bg-[#101828] p-3 rounded-md max-w-80 gap-1 z-50"
                    side="top"
                    align="end"
                  >
                    {tooltipHead && <p className="text-white text-xs font-[600]">{tooltipHead}</p>}
                    {tooltipBody && <p className="text-[#D0D5DD] text-xs font-[500]">{tooltipBody}</p>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </div>
        </div>
        {touched && error && <p className="text-sm font-normal text-[--error-500]">{error}</p>}
        {helpText}
      </div>
    )
  }
)

InputFieldObfuscated.displayName = 'InputWithValueObfuscated'

export { InputFieldObfuscated }

InputFieldObfuscated.propTypes = {
  icon: PropTypes.node,
  helpText: PropTypes.node,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  touched: PropTypes.bool,
  error: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  tooltipHead: PropTypes.string,
  tooltipBody: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  type: PropTypes.string,
  dataTestId: PropTypes.string,
  formatBeforeMaskFN: PropTypes.func,
}
